import { useState, useMemo } from "react"
import { EmailViewerDialog } from "@/components/EmailViewerDialog"
import { Link } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Phone,
  Mail,
  MessageSquare,
  StickyNote,
  Activity,
  Loader2,
  Trash2,
  ArrowDownLeft,
  ArrowUpRight,
  Sparkles,
  FileText,
  Star,
  Zap,
  Play,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Inbox,
  XCircle,
} from "lucide-react"
import {
  communicationsApi,
  type Communication,
  type CommunicationType,
  type MediaAttachment,
} from "@/lib/api"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { formatDateTime, timeAgo } from "@shared/utils/date"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"

// ---- Icons + colours per type ----
const TYPE_ICON: Record<CommunicationType, typeof Phone> = {
  call: Phone,
  email: Mail,
  sms: MessageSquare,
  note: StickyNote,
  activity: Activity,
  lead_submission: Inbox,
}

const TYPE_TONE: Record<CommunicationType, { bg: string; ring: string; icon: string }> = {
  call: { bg: "bg-emerald-100", ring: "ring-emerald-200", icon: "text-emerald-600" },
  email: { bg: "bg-blue-100", ring: "ring-blue-200", icon: "text-blue-600" },
  sms: { bg: "bg-purple-100", ring: "ring-purple-200", icon: "text-purple-600" },
  note: { bg: "bg-amber-100", ring: "ring-amber-200", icon: "text-amber-600" },
  activity: { bg: "bg-rc-grey-100", ring: "ring-rc-grey-200", icon: "text-rc-grey-600" },
  lead_submission: { bg: "bg-sky-100", ring: "ring-sky-200", icon: "text-sky-600" },
}

const TYPE_TITLE: Record<CommunicationType, string> = {
  call: "Phone Call",
  email: "Email",
  sms: "SMS Sent",
  note: "Note Added",
  activity: "Activity",
  lead_submission: "Web Lead",
}

const DIRECTION_TONE = {
  inbound: "bg-blue-100 text-blue-700",
  outbound: "bg-slate-100 text-slate-700",
} as const

export type CommunicationTimelineProps = {
  contactId?: string
  dealId?: string
  type?: CommunicationType
  showAddNote?: boolean
  showContext?: boolean
  title?: string
  limit?: number
  /** Called when the "View all" link at the top is clicked. */
  onViewAll?: () => void
  /** If provided, "View all" becomes a Link to this path. */
  viewAllHref?: string
}

export function CommunicationTimeline({
  contactId,
  dealId,
  type,
  showAddNote = false,
  showContext = false,
  title = "Timeline",
  limit,
  onViewAll,
  viewAllHref,
}: CommunicationTimelineProps) {
  const query = useQuery({
    queryKey: ["communications", { contactId, dealId, type }],
    queryFn: () =>
      communicationsApi.list({
        ...(contactId ? { contactId } : {}),
        ...(dealId ? { dealId } : {}),
        ...(type ? { type } : {}),
        pageSize: 100,
      }),
  })

  const items = limit ? query.data?.items.slice(0, limit) : query.data?.items
  const totalCount = query.data?.items.length ?? 0
  const hasMore = limit ? totalCount > limit : false

  return (
    <div className="space-y-3">
      {/* Add note quick input */}
      {showAddNote && contactId && <AddNoteBox contactId={contactId} dealId={dealId} />}

      {/* Timeline card */}
      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {(onViewAll || viewAllHref || hasMore) &&
            (viewAllHref ? (
              <Link to={viewAllHref} className="text-xs font-medium text-blue-600 hover:text-blue-700">
                View all
              </Link>
            ) : onViewAll ? (
              <button
                type="button"
                onClick={onViewAll}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                View all
              </button>
            ) : null)}
        </div>

        {query.isLoading ? (
          <div className="grid place-items-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : query.error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            Error loading timeline
          </div>
        ) : !items?.length ? (
          <div className="grid place-items-center rounded-md border border-dashed border-border py-10 text-center">
            <StickyNote className="text-muted-foreground/50 mb-2 h-6 w-6" />
            <p className="text-sm font-medium text-foreground">No communications yet</p>
            <p className="text-muted-foreground mt-1 text-xs">
              {showAddNote
                ? "Add a note above to start the timeline."
                : "Calls, emails, SMS and notes will appear here."}
            </p>
          </div>
        ) : (
          <TimelineList items={items} showContext={showContext} />
        )}

        {(onViewAll || viewAllHref) && items && items.length > 0 && (
          <div className="mt-4 border-t border-border pt-3 text-center">
            {viewAllHref ? (
              <Link
                to={viewAllHref}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                View all communications →
              </Link>
            ) : (
              <button
                type="button"
                onClick={onViewAll}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                View all communications →
              </button>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

// ============================================================
// Add-note quick input (blue-highlighted, at top)
// ============================================================
function AddNoteBox({ contactId, dealId }: { contactId: string; dealId?: string }) {
  const [text, setText] = useState("")
  const [expanded, setExpanded] = useState(false)
  const qc = useQueryClient()

  const add = useMutation({
    mutationFn: () =>
      communicationsApi.addNote({ contactId, dealId: dealId ?? null, body: text.trim() }),
    onSuccess: () => {
      setText("")
      setExpanded(false)
      qc.invalidateQueries({ queryKey: ["communications"] })
      qc.invalidateQueries({ queryKey: ["contact", contactId] })
      qc.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="group flex w-full items-center gap-3 rounded-lg border border-border bg-white px-4 py-3 text-left transition-colors hover:border-blue-400 hover:bg-blue-50/30"
      >
        <div className="grid h-8 w-8 place-items-center rounded-full bg-amber-100">
          <Star className="h-4 w-4 text-amber-500" />
        </div>
        <span className="text-sm text-muted-foreground group-hover:text-foreground">
          Add a note about this contact…
        </span>
      </button>
    )
  }

  return (
    <Card className="p-4 ring-2 ring-blue-500/20">
      <Textarea
        rows={3}
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your note (visible to the team)…"
        className="mb-2 resize-none border-0 shadow-none focus-visible:ring-0"
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setExpanded(false)
            setText("")
          }}
          disabled={add.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => add.mutate()}
          disabled={add.isPending || !text.trim()}
        >
          {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Add note
        </Button>
      </div>
    </Card>
  )
}

// ============================================================
// Timeline list with vertical connecting line
// ============================================================
function TimelineList({
  items,
  showContext,
}: {
  items: Communication[]
  showContext: boolean
}) {
  const [emailId, setEmailId] = useState<string | null>(null)
  return (
    <>
      <ol className="relative space-y-5">
        {/* Vertical connecting line — runs through centre of icons */}
        <div
          className="absolute left-[17px] top-5 bottom-3 w-0.5 rounded-full bg-rc-grey-200"
          aria-hidden="true"
        />

        {items.map((c) => (
          <TimelineItem
            key={c.id}
            item={c}
            showContext={showContext}
            onOpenEmail={setEmailId}
          />
        ))}
      </ol>
      <EmailViewerDialog
        communicationId={emailId}
        open={!!emailId}
        onOpenChange={(o) => !o && setEmailId(null)}
      />
    </>
  )
}

function TimelineItem({
  item,
  showContext,
  onOpenEmail,
}: {
  item: Communication
  showContext: boolean
  onOpenEmail?: (id: string) => void
}) {
  const { user } = useAuth()
  const qc = useQueryClient()

  // Special icon overrides for certain provider events
  let displayType = item.type
  let overrideIcon: typeof Phone | null = null
  let overrideTitle: string | null = null
  let overrideTone: string | null = null

  if (item.type === "activity") {
    const bodyLower = item.body?.toLowerCase() ?? ""
    const subjectLower = item.subject?.toLowerCase() ?? ""
    if (subjectLower.includes("closed as lost") || bodyLower.includes("closed as lost")) {
      overrideIcon = XCircle
      overrideTitle = item.subject || "Deal Closed as Lost"
      overrideTone = "bg-red-100 text-red-600"
    } else if (bodyLower.includes("document")) {
      overrideIcon = FileText
      overrideTitle = "Document Uploaded"
      overrideTone = "bg-orange-100 text-orange-600"
    } else if (bodyLower.includes("stage") || bodyLower.includes("moved")) {
      overrideIcon = Zap
      overrideTitle = "Deal Stage Updated"
      overrideTone = "bg-emerald-100 text-emerald-600"
    }
  }
  if (item.type === "email") {
    if (item.direction === "inbound") overrideTitle = "Email Received"
    else overrideTitle = "Email Sent"
  }
  if (item.type === "call") {
    overrideTitle = "Phone Call"
  }
  if (item.type === "sms") {
    if (item.direction === "inbound") overrideTitle = "SMS Received"
    else overrideTitle = "SMS Sent"
  }
  if (item.type === "note") {
    overrideTitle = "Note"
  }
  if (item.type === "lead_submission") {
    // subject is 'New lead — <source>' or 'Lead re-submission — <source>'
    overrideTitle = item.subject || "Web Lead"
  }

  const Icon = overrideIcon ?? TYPE_ICON[displayType]
  const tone = TYPE_TONE[displayType]
  const canDelete =
    user &&
    (["admin", "manager"].includes(user.role) || item.createdBy === user.id)

  const remove = useMutation({
    mutationFn: () => communicationsApi.remove(item.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["communications"] })
      qc.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })

  const contactName =
    item.contactBusinessName ??
    (item.contactFirstName
      ? `${item.contactFirstName} ${item.contactLastName ?? ""}`.trim()
      : null)

  // Split "X days ago" and clock time for two-line right column
  const relative = timeAgo(item.createdAt)
  const clockTime = new Date(item.createdAt * 1000).toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Australia/Brisbane",
  })

  return (
    <li className="relative flex gap-4">
      {/* Icon circle */}
      <div
        className={cn(
          "relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-full ring-4 ring-white",
          overrideTone ?? tone.bg
        )}
      >
        <Icon className={cn("h-4 w-4", overrideTone ? "" : tone.icon)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        {/* Header row: title + direction + right-side timestamp */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                {overrideTitle ?? TYPE_TITLE[displayType]}
              </span>
              {item.direction && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium capitalize",
                    DIRECTION_TONE[item.direction]
                  )}
                >
                  {item.direction === "inbound" ? (
                    <ArrowDownLeft className="h-2.5 w-2.5" />
                  ) : (
                    <ArrowUpRight className="h-2.5 w-2.5" />
                  )}
                  {item.direction}
                </span>
              )}
              {item.aiSentiment && (
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium",
                    item.aiSentiment.toLowerCase().includes("pos")
                      ? "bg-emerald-100 text-emerald-700"
                      : item.aiSentiment.toLowerCase().includes("neg")
                        ? "bg-red-100 text-red-700"
                        : "bg-slate-100 text-slate-700"
                  )}
                >
                  {item.aiSentiment}
                </span>
              )}
            </div>
          </div>
          <span
            className="whitespace-nowrap text-xs text-muted-foreground"
            title={formatDateTime(item.createdAt)}
          >
            {relative}
          </span>
        </div>

        {/* Second row: body preview + right-side clock time */}
        <div className="mt-1 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {/* Type-specific body */}
            {item.type === "sms" ? (
              <SmsBody item={item} />
            ) : item.type === "call" ? (
              <CallBody item={item} />
            ) : item.type === "email" ? (
              <button
                type="button"
                onClick={() => onOpenEmail?.(item.id)}
                className="group -mx-2 -my-1 w-full rounded-md px-2 py-1 text-left transition-colors hover:bg-rc-grey-50"
                title="Open email"
              >
                {item.subject && (
                  <p className="text-foreground group-hover:text-rc-blue-700 truncate text-sm font-medium">
                    {item.subject}
                  </p>
                )}
                {item.body && (
                  <p className="text-muted-foreground line-clamp-2 whitespace-pre-wrap text-sm">
                    {item.body}
                  </p>
                )}
                <p className="text-rc-blue-600 mt-1 text-[11px] font-medium opacity-0 transition-opacity group-hover:opacity-100">
                  Click to open full email →
                </p>
              </button>
            ) : (
              <>
                {item.subject && (
                  <p className="truncate text-sm text-foreground">{item.subject}</p>
                )}
                {item.body && (
                  <p className="line-clamp-2 text-sm text-muted-foreground whitespace-pre-wrap">
                    {item.body}
                  </p>
                )}
              </>
            )}

            {showContext && (contactName || item.dealName) && (
              <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {contactName && (
                  <Link
                    to={`/contacts/${item.contactId}`}
                    className="hover:text-foreground"
                  >
                    → {contactName}
                  </Link>
                )}
                {item.dealName && item.dealId && (
                  <Link
                    to={`/deals/${item.dealId}`}
                    className="hover:text-foreground"
                  >
                    → {item.dealName}
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="whitespace-nowrap text-xs text-muted-foreground">{clockTime}</span>
            {canDelete && (
              <button
                onClick={() => {
                  if (confirm("Delete this entry?")) remove.mutate()
                }}
                className="text-muted-foreground/40 hover:text-destructive"
                aria-label="Delete"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

// ============================================================
// SMS bubble renderer — iMessage style + MMS media grid
// ============================================================
function SmsBody({ item }: { item: Communication }) {
  const isOutbound = item.direction === "outbound"
  // Legacy: some very old outbound rows may still have provider_name='aircall_pending'.
  // New sends write 'aircall' directly, so this only affects backfill.
  const isPending = item.providerName === "aircall_pending"
  const media = useMemo<MediaAttachment[]>(() => {
    if (!item.mediaAttachments) return []
    try {
      return JSON.parse(item.mediaAttachments) as MediaAttachment[]
    } catch {
      return []
    }
  }, [item.mediaAttachments])

  // Hide legacy "[📎 filename]" placeholder bodies when we have media—the media
  // itself is the message. Only show real user-typed text.
  const bodyText =
    item.body && !/^\[\ud83d\udcce[^\]]+\]$/.test(item.body.trim())
      ? item.body
      : null

  return (
    <div className={cn("mt-1 flex", isOutbound ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm",
          isOutbound
            ? "bg-blue-500 text-white rounded-br-md"
            : "bg-rc-grey-100 text-foreground rounded-bl-md"
        )}
      >
        {bodyText && (
          <div className="whitespace-pre-wrap break-words">{bodyText}</div>
        )}

        {media.length > 0 && (
          <div className={cn("mt-2 grid gap-2", media.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
            {media.map((m, idx) => (
              <SmsMedia key={m.storageKey} commId={item.id} index={idx} media={m} />
            ))}
          </div>
        )}

        {isPending && (
          <div className={cn("mt-1 text-[10px]", isOutbound ? "text-blue-100" : "text-muted-foreground")}>
            Sending…
          </div>
        )}
      </div>
    </div>
  )
}

function SmsMedia({
  commId,
  index,
  media,
}: {
  commId: string
  index: number
  media: MediaAttachment
}) {
  const src = communicationsApi.mediaUrl(commId, index)
  const ct = media.contentType || "application/octet-stream"
  if (ct.startsWith("image/")) {
    return (
      <a href={src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-lg">
        <img
          src={src}
          alt={media.fileName}
          className="h-40 w-full object-cover"
          loading="lazy"
        />
      </a>
    )
  }
  if (ct.startsWith("video/")) {
    return (
      <video src={src} controls className="h-40 w-full rounded-lg bg-black" />
    )
  }
  if (ct.startsWith("audio/")) {
    return <audio src={src} controls className="w-full" />
  }
  // PDFs & everything else: chip link
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 rounded-lg bg-white/20 px-2 py-1.5 text-xs hover:bg-white/30"
    >
      <Paperclip className="h-3.5 w-3.5" />
      <span className="truncate">{media.fileName}</span>
    </a>
  )
}

// ============================================================
// Call renderer — duration, AI summary, topics, transcript toggle, play recording
// ============================================================
function CallBody({ item }: { item: Communication }) {
  // Body may look like: "Aircall inbound call — Andy Ruyl\n\n---\nTranscript:\n[Agent] Hi\n[Customer] Hello\u2026"
  const { header, transcript } = useMemo(() => splitTranscript(item.body), [item.body])
  const topics = useMemo<string[]>(() => {
    if (!item.aiTopics) return []
    try {
      const parsed = JSON.parse(item.aiTopics)
      return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === "string") : []
    } catch {
      return []
    }
  }, [item.aiTopics])

  const [showTranscript, setShowTranscript] = useState(false)
  const [playing, setPlaying] = useState(false)

  const hasRecording = item.providerName === "aircall" && !!item.providerRef
  const isTranscribing = item.aiStatus === "pending"

  return (
    <div className="space-y-2">
      {header && (
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{header}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {item.durationSeconds !== null && item.durationSeconds !== undefined && (
          <span className="inline-flex items-center gap-1 rounded-md bg-rc-grey-100 px-2 py-0.5 text-[11px] font-medium text-foreground">
            {formatDuration(item.durationSeconds)}
          </span>
        )}
        {isTranscribing && (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            <Loader2 className="h-3 w-3 animate-spin" />
            Transcribing…
          </span>
        )}
        {hasRecording && (
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-200"
          >
            <Play className="h-3 w-3" />
            {playing ? "Hide player" : "Play recording"}
          </button>
        )}
        {transcript && (
          <button
            type="button"
            onClick={() => setShowTranscript((s) => !s)}
            className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 hover:bg-blue-200"
          >
            {showTranscript ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Hide transcript
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Show transcript
              </>
            )}
          </button>
        )}
      </div>

      {playing && hasRecording && (
        <audio
          src={communicationsApi.recordingUrl(item.id)}
          controls
          autoPlay
          className="w-full"
        />
      )}

      {topics.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {topics.map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700"
            >
              {t}
            </span>
          ))}
        </div>
      )}

      {item.aiSummary && (
        <div className="rounded-md border-l-2 border-purple-400 bg-purple-50 p-2 text-xs text-purple-900">
          <div className="mb-0.5 flex items-center gap-1 font-medium">
            <Sparkles className="h-3 w-3" />
            AI Summary
          </div>
          <div className="whitespace-pre-wrap">{item.aiSummary}</div>
        </div>
      )}

      {showTranscript && transcript && (
        <div className="rounded-md border border-border bg-white p-2 text-xs">
          <div className="mb-1 font-medium text-foreground">Transcript</div>
          <div className="space-y-1">
            {transcript.split("\n").map((line, i) => {
              const isCustomer = line.startsWith("[Customer]")
              const isAgent = line.startsWith("[Agent]")
              const text = line.replace(/^\[(Customer|Agent)\]\s*/, "")
              if (!text.trim()) return null
              return (
                <div
                  key={i}
                  className={cn(
                    "rounded px-2 py-1",
                    isCustomer
                      ? "bg-emerald-50 text-emerald-900"
                      : isAgent
                        ? "bg-blue-50 text-blue-900"
                        : "bg-rc-grey-50 text-foreground"
                  )}
                >
                  <span className="mr-1 text-[10px] font-medium uppercase opacity-60">
                    {isCustomer ? "Customer" : isAgent ? "Agent" : ""}
                  </span>
                  {text}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Body is stored as "<header>\n\n---\nTranscript:\n<utterances>" by the webhook.
 * Split cleanly for rendering.
 */
function splitTranscript(body: string | null): {
  header: string
  transcript: string | null
} {
  if (!body) return { header: "", transcript: null }
  const marker = "---\nTranscript:\n"
  const idx = body.indexOf(marker)
  if (idx === -1) return { header: body, transcript: null }
  return {
    header: body.slice(0, idx).replace(/\n+$/, ""),
    transcript: body.slice(idx + marker.length).trim() || null,
  }
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}
